import React, { useLayoutEffect, useRef, useState } from "react";
import { formatSqlColumnHeader } from "../../../utils/formatSqlColumnHeader";
import { formatChartAxisDateValue as formatDateValue } from "../../../utils/formatChartAxisDate";
import {
  formatChartTooltipValue,
  parseChartTooltipNumber,
} from "../../ChatPage/ChatSectionChartHelpers";
import {
  isSqlAmountColumn,
  isSqlDateColumn,
  isSqlNumericColumn,
  isSqlPureNumericValue,
  sqlSortColumnsMatch,
} from "../../../utils/sqlResultTableSort";
import FilterListIcon from "@mui/icons-material/FilterList";
import SqlResultColumnSortDropdown from "./SqlResultColumnSortDropdown";
import "./SqlResultDataTable.scrollbar.css";
import "./SqlResultDataTable.tables.css";

function isDateCellValue(value) {
  if (value == null || value === "") return false;
  return formatDateValue(value) !== value;
}

function getSqlColumnClass(col, rawResults) {
  if (isSqlDateColumn(col, rawResults)) return "date";
  if (isSqlAmountColumn(col) || isSqlNumericColumn(col, rawResults)) return "numeric";
  return "";
}

const SORT_DROPDOWN_CONTAINER_BUFFER_PX = 12;

/** Expands scroll container min-height while sort menu overflows (low row tables). */
function useSortDropdownContainerMinHeight(
  containerRef,
  isSortMenuOpen,
  openDropdownKey,
  rowCount
) {
  const [minHeight, setMinHeight] = useState(null);

  useLayoutEffect(() => {
    if (!isSortMenuOpen) {
      setMinHeight(null);
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      setMinHeight(null);
      return undefined;
    }

    let rafId = 0;

    const measure = () => {
      const menu = container.querySelector(".table-sort-dropdown-menu");
      if (!menu) return;

      const containerRect = container.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const overflowBottom = menuRect.bottom - containerRect.bottom;

      if (overflowBottom > 0) {
        setMinHeight(
          container.offsetHeight + overflowBottom + SORT_DROPDOWN_CONTAINER_BUFFER_PX
        );
      }
      // Keep expanded min-height while open; clearing when overflow resolves would collapse the container.
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    scheduleMeasure();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(container);
      const menu = container.querySelector(".table-sort-dropdown-menu");
      if (menu) resizeObserver.observe(menu);
    }

    window.addEventListener("resize", scheduleMeasure);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [containerRef, isSortMenuOpen, openDropdownKey, rowCount]);

  return minHeight;
}

/**
 * Core scroll + <table> for SQL row arrays (used inside SqlResultDataTable and full-view modal).
 *
 * @param {"message"|"modal"|"panel"} wrapperMode — "message" wraps in message-wrapper-div; "panel" is inside sql-result-panel; "modal" is full-view body only.
 */
export default function SqlResultTableBody({
  rawResults,
  columns: columnsOverride,
  formatScalarForDisplay,
  renderFooter,
  wrapperMode = "message",
  sort,
}) {
  const {
    messageId,
    sessionId,
    openDropdownKey,
    onToggleSortDropdown,
    onApplySort,
    isSortLoading,
    activeSort,
    queryRowCount,
    loadedCount,
    totalRowCount,
  } = sort || {};

  const isSortMenuOpen = Boolean(openDropdownKey);
  const tableContainerRef = useRef(null);
  const sortMenuMinHeight = useSortDropdownContainerMinHeight(
    tableContainerRef,
    isSortMenuOpen,
    openDropdownKey,
    rawResults?.length ?? 0
  );

  if (!rawResults || rawResults.length === 0) return null;

  const formatPlain = formatScalarForDisplay ?? ((text) => (text == null ? "-" : String(text)));

  const formatNumericCell = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return formatChartTooltipValue(value);
    }
    const parsed = parseChartTooltipNumber(value);
    if (Number.isFinite(parsed)) {
      return formatChartTooltipValue(parsed);
    }
    return formatPlain(value);
  };

  const columns =
    columnsOverride?.length > 0 ? columnsOverride : Object.keys(rawResults[0] || {});

  const canSort = Boolean(messageId && sessionId && onApplySort);

  const tableContainerClassName =
    wrapperMode === "modal"
      ? "data-table-container full-view-table-container emcure-data-table-scrollbar"
      : `data-table-container data-table-container-wrapper emcure-data-table-scrollbar${
          sortMenuMinHeight != null ? " data-table-container-wrapper--sort-expanded" : ""
        }`;

  const tableContainerStyle =
    sortMenuMinHeight != null ? { minHeight: sortMenuMinHeight } : undefined;

  const tableBlock = (
    <>
      <div
        ref={tableContainerRef}
        className={tableContainerClassName}
        style={tableContainerStyle}
      >
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col, idx) => {
                const columnClass = getSqlColumnClass(col, rawResults);
                const headerLabel = formatSqlColumnHeader(col);
                const isNumericColumn = isSqlNumericColumn(col, rawResults);
                const sortDropdownKey = `${col}`;
                const isSortMenuOpen = openDropdownKey === sortDropdownKey;
                const activeSortOrder =
                  activeSort && sqlSortColumnsMatch(activeSort.columnKey, col)
                    ? activeSort.sortOrder
                    : null;
                const showSortControl = isNumericColumn || Boolean(activeSortOrder);

                if (!showSortControl) {
                  return (
                    <th key={idx} className={columnClass}>
                      {headerLabel}
                    </th>
                  );
                }

                return (
                  <th key={idx} className={`${columnClass} sql-sortable-th`}>
                    <div className="sql-sortable-header-inner">
                      <span className="sql-sortable-header-label">{headerLabel}</span>
                      <div className="table-sort-dropdown-wrapper">
                        <button
                          type="button"
                          className="sql-sort-icon-trigger"
                          onClick={() => onToggleSortDropdown?.(col)}
                          disabled={!canSort}
                          title={
                            !canSort
                              ? "Sort is available only for saved messages"
                              : `Sort ${headerLabel}`
                          }
                          aria-expanded={isSortMenuOpen}
                          aria-haspopup="dialog"
                        >
                          <FilterListIcon
                            className={`sql-sort-filter-icon${
                              activeSortOrder ? " sql-sort-filter-icon--active" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                        <SqlResultColumnSortDropdown
                          columnName={col}
                          isOpen={isSortMenuOpen}
                          onApply={onApplySort}
                          isLoading={isSortLoading?.(col)}
                          defaultSortOrder={activeSortOrder || "asc"}
                          queryRowCount={queryRowCount}
                          loadedCount={loadedCount}
                          totalRowCount={totalRowCount}
                        />
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rawResults.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {columns.map((col, colIdx) => {
                  const value = row[col];
                  const isAmountColumn = isSqlAmountColumn(col);
                  const columnTypeClass = getSqlColumnClass(col, rawResults);
                  const dateFormatted = formatDateValue(value);
                  const isDateValue =
                    columnTypeClass === "date" || isDateCellValue(value);
                  const showAmountFormat =
                    isAmountColumn && !isDateValue && isSqlPureNumericValue(value);
                  const alignNumeric =
                    columnTypeClass === "numeric" ||
                    (!isDateValue && isSqlNumericColumn(col, rawResults));

                  return (
                    <td
                      key={colIdx}
                      className={
                        isDateValue ? "date" : alignNumeric ? "numeric" : ""
                      }
                      title={isDateValue ? String(dateFormatted) : formatPlain(value)}
                    >
                      {isDateValue
                        ? String(dateFormatted)
                        : showAmountFormat
                          ? formatNumericCell(value)
                          : formatPlain(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {wrapperMode !== "modal" && renderFooter != null ? renderFooter : null}
    </>
  );

  if (wrapperMode === "modal") {
    return (
      <>
        {tableBlock}
        {renderFooter != null ? renderFooter : null}
      </>
    );
  }

  if (wrapperMode === "panel") {
    return <div className="message-wrapper-div message-wrapper-div--panel">{tableBlock}</div>;
  }

  return <div className="message-wrapper-div">{tableBlock}</div>;
}
